import React from "react";
import { Control, Controller } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import FileDropZone from "./FileDropZone";
import {
  ConfigurableFieldDefinition,
  ConfigurableFormValues,
  ConfigurableOption,
  ResolvedConfigurableField,
} from "../types/configurableUpload";

interface ConfigurableFieldRendererProps {
  field: ConfigurableFieldDefinition | ResolvedConfigurableField;
  control: Control<ConfigurableFormValues>;
  disabled?: boolean;
  isPending?: boolean;
  onFieldChange: (
    field: ConfigurableFieldDefinition | ResolvedConfigurableField,
    value: string | string[] | File | null,
  ) => void;
}

const selectableTypes = new Set([
  "deal_internal_name",
  "deal_name",
  "quarter",
  "single_select",
]);

const getFieldEnabled = (
  field: ConfigurableFieldDefinition | ResolvedConfigurableField,
) => {
  if ("enabled" in field) {
    return field.enabled;
  }
  return field.editable;
};

const isExitedOption = (option?: ConfigurableOption | null) => {
  if (!option?.metadata) return false;
  return option.metadata.isActive === false || option.metadata.status === "exited";
};

const renderOptionLabel = (option: ConfigurableOption) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
    <Typography component="span" sx={{ flex: 1 }}>
      {option.label}
    </Typography>
    {option.metadata?.status && (
      <Chip
        size="small"
        color={isExitedOption(option) ? "default" : "success"}
        label={option.metadata.status}
      />
    )}
  </Box>
);

const ConfigurableFieldRenderer: React.FC<ConfigurableFieldRendererProps> = ({
  field,
  control,
  disabled = false,
  isPending = false,
  onFieldChange,
}) => {
  if (!field.visible || field.fieldType === "hidden") {
    return null;
  }

  const label = field.label ?? field.key;
  const fieldDisabled = disabled || isPending || !getFieldEnabled(field);
  const validationMessage =
    "validationMessage" in field ? field.validationMessage : null;
  const helperText = validationMessage ?? field.helpText ?? undefined;

  if (field.fieldType === "file") {
    return (
      <Controller
        name={field.key}
        control={control}
        render={({ field: controllerField, fieldState }) => {
          const handleFile = (selectedFile: File | null) => {
            controllerField.onChange(selectedFile);
            onFieldChange(field, selectedFile);
          };

          return (
            <FileDropZone
              value={controllerField.value instanceof File ? controllerField.value : null}
              accept={field.accept ?? undefined}
              disabled={fieldDisabled}
              isPending={isPending}
              label={label}
              helperText={fieldState.error?.message ?? helperText}
              error={!!fieldState.error || !!validationMessage}
              onChange={handleFile}
              onRemove={() => handleFile(null)}
            />
          );
        }}
      />
    );
  }

  if (field.fieldType === "multi_select") {
    return (
      <Controller
        name={field.key}
        control={control}
        render={({ field: controllerField, fieldState }) => {
          const values = Array.isArray(controllerField.value)
            ? controllerField.value
            : [];
          const selectedOptions = field.options.filter((option) =>
            values.includes(option.value),
          );

          return (
            <Autocomplete
              multiple
              options={field.options}
              value={selectedOptions}
              disabled={fieldDisabled}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, selected) => {
                const nextValue = selected.map((option) => option.value);
                controllerField.onChange(nextValue);
                onFieldChange(field, nextValue);
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  {renderOptionLabel(option)}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  placeholder={field.placeholder ?? undefined}
                  required={field.required}
                  error={!!fieldState.error || !!validationMessage}
                  helperText={fieldState.error?.message ?? helperText}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isPending && <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          );
        }}
      />
    );
  }

  if (selectableTypes.has(field.fieldType)) {
    return (
      <Controller
        name={field.key}
        control={control}
        render={({ field: controllerField, fieldState }) => {
          const selectedOption =
            field.options.find((option) => option.value === controllerField.value) ??
            null;

          return (
            <Autocomplete
              options={field.options}
              value={selectedOption}
              disabled={fieldDisabled}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, selected) => {
                const nextValue = selected?.value ?? null;
                controllerField.onChange(nextValue);
                onFieldChange(field, nextValue);
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  {renderOptionLabel(option)}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  placeholder={field.placeholder ?? undefined}
                  required={field.required}
                  error={!!fieldState.error || !!validationMessage}
                  helperText={fieldState.error?.message ?? helperText}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isPending && <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />}
                        {isExitedOption(selectedOption) && (
                          <Chip size="small" label="exited" sx={{ mr: 1 }} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          );
        }}
      />
    );
  }

  return (
    <Controller
      name={field.key}
      control={control}
      render={({ field: controllerField, fieldState }) => (
        <TextField
          {...controllerField}
          value={typeof controllerField.value === "string" ? controllerField.value : ""}
          fullWidth
          label={label}
          placeholder={field.placeholder ?? undefined}
          required={field.required}
          disabled={fieldDisabled}
          error={!!fieldState.error || !!validationMessage}
          helperText={fieldState.error?.message ?? helperText}
          InputProps={{
            endAdornment: isPending ? <CircularProgress color="inherit" size={16} /> : undefined,
          }}
          onChange={(event) => {
            controllerField.onChange(event);
            onFieldChange(field, event.target.value);
          }}
        />
      )}
    />
  );
};

export default ConfigurableFieldRenderer;
